import type { WidgetItemSetting, WidgetOptionValue, WidgetSettings } from '../../../store/dashboardStore';

/**
 * 위젯 «보이는 항목» · 선택지 정의 (2026-09-15 · 대시보드 시안 v2 · 단위 V1).
 *
 * 근거: `_workspace/dashboard-v2/web-plan.md` §1.2 · §4 규칙 D1~D4 · 사용자 결정 W-Q1(1단계 8위젯) ·
 *   W-Q2(팀/전사는 서버 권한과 같은 사람만) · W-Q3(빠른 필터는 이미 칩이 있는 위젯만) · Q-8(부서 범위는 서버 전 숨김).
 *
 * ★★★ **기본값 = 지금 그 위젯이 그리는 것** (규칙 D1). 설정을 한 번도 안 건드린 사람은 v2 판에서도 화면이
 *     바뀌지 않아야 한다. 그래서 시안 목록에 없던 현행 항목(근태 관리 「사용 연차」)도 지우지 않고 켜 둔다.
 * ★★★ **막힌 항목은 저장값을 지우지 않는다** (규칙 D2). 권한 · 메뉴 · 서버 기능이 돌아오면 그대로 되살아난다 —
 *     그래서 `on`(저장값)과 `visible`(지금 그릴지)을 따로 준다.
 * ★★ 저장값에 **모르는 key** 가 있어도 버리지 않는다 — 판이 오갈 때(롤백 · 옛 탭) 사라지지 않게.
 * ★ 이 파일은 React · 네트워크를 모른다. 막힘을 판정할 재료(메뉴 · 범위 · 서버 기능)는 부르는 쪽이 `ctx` 로 넣는다.
 * ⚠ 샘플 숫자는 여기 없다 — 항목 이름 · 선택지뿐이다.
 */

/* ── 막힘 ─────────────────────────────────────────────────────────────────── */

/** 막힌 이유 네 갈래 (정본 제안 16 — 문구 규칙). */
export type LockKind = 'permission' | 'menu' | 'company' | 'not-computed';

export interface Lock {
  kind: LockKind;
  text: string;
}

/**
 * 서버에 아직 없는 칸 · 기능 (web-plan §5.2). 서버가 생기면 부르는 쪽이 `ctx.serverFeatures` 에 넣는다.
 * ★ 이름은 요청 번호가 아니라 **무엇이 생기는가** 로 짓는다 — 요청 번호는 바뀌어도 기능은 안 바뀐다.
 */
export type ServerFeature =
  | 'board-confirm-mine'        // 게시판 R8 — 내 차례 확인 공지
  | 'focus-time'                // DR16
  | 'reject-comment'            // DR10
  | 'attendance-year'           // DR8 — 올해 근무일수 · 누적
  | 'attendance-team-summary'   // DR8 — 팀 · 전사 합계
  | 'week-summary-team'         // DR9 — 팀 합계
  | 'division-scope';           // DR7 — 부서 범위

export type ScopeKey = 'team' | 'division' | 'company';

/** 막힘을 판정할 재료. 모르는 칸은 비워 둔다 — 비면 «막지 않음»(메뉴) 또는 «아직 없음»(범위 · 서버)이다. */
export interface ItemContext {
  /** 메뉴가 켜졌나. 안 주면 전부 켜진 것으로 본다(메뉴 막힘 규칙과 같은 fail-open). */
  menuEnabled?: (menuKey: string) => boolean;
  /**
   * 서버가 알려 준 «내가 볼 수 있는 범위»(DR6). 안 주면 `null` — 그때는 «나»만 열고
   * 팀 · 부서 · 전사는 이유와 함께 막는다(W-Q2). 403 으로 떠보지 않는다.
   */
  scopes?: Readonly<Record<ScopeKey, boolean>> | null;
  /** 회사가 지각 판정을 쓰는가(DR11). 모르면 막지 않는다. */
  lateJudged?: boolean | null;
  /** 서버에 생긴 기능. */
  serverFeatures?: ReadonlySet<ServerFeature>;
}

interface Requires {
  /** 이 메뉴들이 모두 켜져야 한다. label 은 막힘 문구에 들어간다. */
  menus?: ReadonlyArray<{ key: string; label: string }>;
  server?: ServerFeature;
  /** 회사가 지각 판정을 써야 한다. */
  lateJudged?: true;
  /** 이 범위를 볼 수 있어야 한다. */
  scope?: ScopeKey;
}

/* ── 정의 ─────────────────────────────────────────────────────────────────── */

export interface ItemDef {
  key: string;
  label: string;
  /** 이름 아래 한 줄. */
  desc?: string;
  /** 어느 묶음인가 — 한 위젯에 목록이 둘일 수 있다(일정 상세: 보이는 일정 / 한 줄에 보일 것). */
  group: string;
  defaultOn: boolean;
  requires?: Requires;
  /** 막혔을 때 문구를 갈래 기본 문구 대신 쓴다. */
  lockText?: string;
}

export interface ItemGroupDef {
  key: string;
  label: string;
  /** 켤 수 있는 최대 개수(한눈에 지표 5). */
  maxOn?: number;
  /** 최소 켜 둬야 하는 개수 — 다 끄면 위젯이 빈 칸이 되는 목록(일정 탭). */
  minOn?: number;
  /** 순서가 뜻이 없는 목록 — 손잡이를 세우지 않는다(메일 줄 · 일정 탭 · 시안에 ⠿ 가 없다). 기본 true. */
  reorderable?: boolean;
}

export interface ChoiceDef {
  value: string;
  label: string;
  requires?: Requires;
  lockText?: string;
  /** 이 기능이 서버에 생기기 전에는 **아예 안 보인다**(Q-8 — 누를 수 없는 선택지를 두지 않는 경우). */
  hiddenUntil?: ServerFeature;
}

export type OptionDef =
  | { key: string; label: string; kind: 'single'; choices: ChoiceDef[]; defaultValue: string; hint?: string }
  | { key: string; label: string; kind: 'multi'; choices: ChoiceDef[]; defaultValue: string[]; hint?: string }
  | { key: string; label: string; kind: 'toggle'; desc?: string; defaultValue: boolean; hint?: string };

export interface WidgetItemSchema {
  widgetKey: string;
  groups: ItemGroupDef[];
  /** 모든 묶음의 항목 — 묶음 안의 기본 순서는 이 배열 순서다. */
  items: ItemDef[];
  options: OptionDef[];
}

/* ── 막힘 문구 (정본 제안 16) ───────────────────────────────────────────────── */

const SCOPE_LABEL: Record<ScopeKey, string> = { team: '팀', division: '부서', company: '전사' };

function lockOf(req: Requires | undefined, lockText: string | undefined, ctx: ItemContext): Lock | null {
  if (!req) return null;
  // 순서 = 사용자가 풀 수 있는 것부터 알린다: 권한 → 메뉴 → 회사 설정 → 서버.
  if (req.scope) {
    const can = ctx.scopes?.[req.scope] === true;
    if (!can) {
      return { kind: 'permission', text: lockText ?? `${SCOPE_LABEL[req.scope]} 범위는 볼 권한이 있는 사람만 고를 수 있습니다` };
    }
  }
  for (const m of req.menus ?? []) {
    if (ctx.menuEnabled && !ctx.menuEnabled(m.key)) {
      return { kind: 'menu', text: `관리자가 ${m.label} 메뉴를 꺼서 고를 수 없습니다` };
    }
  }
  if (req.lateJudged && ctx.lateJudged === false) {
    return { kind: 'company', text: lockText ?? '회사가 지각 기록을 쓰지 않아 고를 수 없습니다' };
  }
  if (req.server && !ctx.serverFeatures?.has(req.server)) {
    return { kind: 'not-computed', text: lockText ?? '아직 계산하지 않는 값이라 고를 수 없습니다' };
  }
  return null;
}

/* ── 위젯 8종 (W-Q1) ──────────────────────────────────────────────────────── */

const MENU = {
  attendance: { key: 'attendance', label: '근태' },
  approval: { key: 'approval', label: '전자결재' },
  task: { key: 'task', label: '할일' },
  calendar: { key: 'calendar', label: '캘린더' },
  mail: { key: 'mail', label: '메일' },
} as const;

const ITEMS = { key: 'items', label: '보이는 항목' } as const;

const ATTENDANCE_SCOPE_LOCK = '팀 · 전사는 근태 담당자 · 관리자만 고릅니다';

export const WIDGET_ITEM_SCHEMAS: Readonly<Record<string, WidgetItemSchema>> = {
  /*
   * 날씨 (2026-09-21 · 시안 v4 확정).
   *
   * ★ 「어디 날씨」는 여기 옵션이 아니라 **고유 설정**(`WidgetOwnSettings`)이다 — 고를 값이
   *   서버에서 오는 사업장 목록이라 붙박이 선택지로 적을 수 없다(회의실 분류와 같은 자리).
   * ★ 시간별 예보를 끄면 **단기예보를 부르지도 않는다** — 공유 한도를 아낀다.
   */
  weather: {
    widgetKey: 'weather',
    groups: [ITEMS],
    items: [
      { key: 'feels', group: 'items', label: '체감 온도', desc: '기온과 다를 때만 보입니다', defaultOn: true },
      { key: 'humidity', group: 'items', label: '습도', defaultOn: true },
      { key: 'pop', group: 'items', label: '강수 확률', defaultOn: true },
      { key: 'wind', group: 'items', label: '바람', defaultOn: true },
      { key: 'hours', group: 'items', label: '시간별 예보', desc: '앞으로 6시간', defaultOn: false },
    ],
    options: [],
  },

  /* 근태 관리 — 현행: 출퇴근 · 잔여 · 사용 · 결재 대기 · 초과 근무 · 6개월 막대 (`AttendanceWidgets.tsx:246-268`). */
  'attendance-summary': {
    widgetKey: 'attendance-summary',
    groups: [ITEMS],
    items: [
      { key: 'punch', group: 'items', label: '출근·퇴근', desc: '시계와 출퇴근 버튼', defaultOn: true },
      { key: 'leaveRemaining', group: 'items', label: '잔여 연차', defaultOn: true },
      { key: 'leaveUsed', group: 'items', label: '사용 연차', defaultOn: true },
      { key: 'leavePending', group: 'items', label: '결재 대기', defaultOn: true },
      { key: 'overtime', group: 'items', label: '초과 근무', defaultOn: true },
      { key: 'workDays', group: 'items', label: '근무일수', defaultOn: false },
      { key: 'workedTotal', group: 'items', label: '누적 근무', defaultOn: false },
      { key: 'late', group: 'items', label: '지각·조퇴', defaultOn: false, requires: { lateJudged: true } },
      { key: 'sixMonths', group: 'items', label: '최근 6개월 연차 사용', desc: '막대', defaultOn: true },
    ],
    options: [
      {
        key: 'period', label: '기간', kind: 'single', defaultValue: 'month',
        choices: [
          { value: 'month', label: '이번 달' },
          { value: 'year', label: '올해', requires: { server: 'attendance-year' } },
        ],
      },
      {
        key: 'scope', label: '범위', kind: 'single', defaultValue: 'self', hint: ATTENDANCE_SCOPE_LOCK,
        choices: [
          { value: 'self', label: '나' },
          { value: 'team', label: '팀', requires: { scope: 'team', server: 'attendance-team-summary' }, lockText: ATTENDANCE_SCOPE_LOCK },
          { value: 'company', label: '전사', requires: { scope: 'company', server: 'attendance-team-summary' }, lockText: ATTENDANCE_SCOPE_LOCK },
        ],
      },
    ],
  },

  /* 이번 주 요약 — 현행: 회의 · 근무 · 처리한 결재 · 회의 비중 · 지난주 (`WorkDetailWidgets.tsx:366-406`). */
  'week-summary': {
    widgetKey: 'week-summary',
    groups: [ITEMS],
    items: [
      { key: 'meeting', group: 'items', label: '회의 시간', defaultOn: true, requires: { menus: [MENU.calendar] } },
      { key: 'worked', group: 'items', label: '근무 시간', defaultOn: true, requires: { menus: [MENU.attendance] } },
      { key: 'approvals', group: 'items', label: '처리한 결재', defaultOn: true, requires: { menus: [MENU.approval] } },
      { key: 'share', group: 'items', label: '회의 비중', desc: '게이지', defaultOn: true, requires: { menus: [MENU.calendar, MENU.attendance] } },
      { key: 'compare', group: 'items', label: '지난주와 비교', desc: '각 줄 오른쪽 배지', defaultOn: true },
      { key: 'focus', group: 'items', label: '집중 시간', defaultOn: false, requires: { server: 'focus-time' } },
    ],
    options: [
      {
        key: 'period', label: '기간', kind: 'single', defaultValue: 'thisWeek',
        choices: [{ value: 'thisWeek', label: '이번 주' }, { value: 'lastWeek', label: '지난주' }],
      },
      {
        key: 'scope', label: '범위', kind: 'single', defaultValue: 'self', hint: '팀 합계는 팀장 이상',
        choices: [
          { value: 'self', label: '나' },
          { value: 'team', label: '팀', requires: { scope: 'team', server: 'week-summary-team' }, lockText: '팀 합계는 팀장 이상만 고를 수 있습니다' },
        ],
      },
    ],
  },

  /* 한눈에 — 현행: 완료 결재 · 하루 평균 근무 · 일정 시간 · 남은 연차 · 진행 프로젝트 (`FullsizeWidgets.tsx:332-391`). */
  'kpi-strip': {
    widgetKey: 'kpi-strip',
    groups: [{ key: 'items', label: '보이는 지표', maxOn: 5 }],
    items: [
      { key: 'approval', group: 'items', label: '완료 결재', defaultOn: true, requires: { menus: [MENU.approval] } },
      { key: 'work', group: 'items', label: '하루 평균 근무', defaultOn: true, requires: { menus: [MENU.attendance] } },
      { key: 'meeting', group: 'items', label: '일정 시간', defaultOn: true, requires: { menus: [MENU.calendar] } },
      { key: 'leave', group: 'items', label: '남은 연차', defaultOn: true },
      { key: 'project', group: 'items', label: '진행 프로젝트', defaultOn: true, requires: { menus: [MENU.task] } },
    ],
    options: [
      {
        key: 'period', label: '기간', kind: 'single', defaultValue: 'month',
        choices: [{ value: 'month', label: '이번 달' }, { value: 'week', label: '이번 주' }],
      },
      { key: 'compare', label: '비교', kind: 'toggle', desc: '지난달과 비교 배지', defaultValue: false },
    ],
  },

  /* 메일 — 현행: 5통 · 칩 기본 「안 읽음」 · 받은편지함 · 줄에 보낸 사람 (`WorkWidgets.tsx:84-128`). 칩은 이미 있다(W-Q3). */
  mail: {
    widgetKey: 'mail',
    groups: [{ ...ITEMS, reorderable: false }],
    items: [
      { key: 'sender', group: 'items', label: '보낸 사람', defaultOn: true },
      { key: 'receivedAt', group: 'items', label: '받은 시각', defaultOn: false },
      { key: 'preview', group: 'items', label: '본문 첫 줄', desc: '제목 아래 한 줄', defaultOn: false },
      { key: 'attachment', group: 'items', label: '첨부 표시', defaultOn: false },
    ],
    options: [
      {
        key: 'count', label: '표시 개수', kind: 'single', defaultValue: '5',
        choices: [{ value: '3', label: '3건' }, { value: '5', label: '5건' }, { value: '8', label: '8건' }],
      },
      {
        key: 'filter', label: '기본 필터', kind: 'single', defaultValue: 'unread',
        hint: '위젯 머리의 칩으로 그 자리에서 바꿀 수도 있습니다',
        choices: [{ value: 'unread', label: '안 읽음' }, { value: 'all', label: '전체' }, { value: 'star', label: '중요' }],
      },
      {
        key: 'folder', label: '메일함', kind: 'single', defaultValue: 'inbox',
        choices: [{ value: 'inbox', label: '받은편지함' }, { value: 'all', label: '모든 편지함' }],
      },
    ],
  },

  /* 캘린더 · 일정 상세 — 현행: 탭 셋 · 기본 「내 일정」 · 오늘 · 5건 · 줄에 유형 색 · 장소 (`CalendarWidgets.tsx:97-159`). 칩은 이미 있다(W-Q3). */
  schedule: {
    widgetKey: 'schedule',
    groups: [
      { key: 'tabs', label: '보이는 일정 · 기본 탭', minOn: 1, reorderable: false },
      { key: 'line', label: '한 줄에 보일 것' },
    ],
    items: [
      { key: 'company', group: 'tabs', label: '회사 주요일정', desc: '교육 · 기념일', defaultOn: true },
      { key: 'mine', group: 'tabs', label: '내 일정', defaultOn: true },
      { key: 'team', group: 'tabs', label: '팀원 일정', defaultOn: true },
      { key: 'location', group: 'line', label: '장소', defaultOn: true },
      { key: 'attendees', group: 'line', label: '참석자 수', defaultOn: false },
      { key: 'typeColor', group: 'line', label: '일정 유형 색', defaultOn: true },
    ],
    options: [
      {
        key: 'count', label: '표시 개수', kind: 'single', defaultValue: '5',
        choices: [{ value: '3', label: '3건' }, { value: '5', label: '5건' }, { value: '8', label: '8건' }],
      },
      {
        key: 'defaultTab', label: '기본 탭', kind: 'single', defaultValue: 'mine',
        choices: [{ value: 'company', label: '회사 주요일정' }, { value: 'mine', label: '내 일정' }, { value: 'team', label: '팀원 일정' }],
      },
      {
        key: 'period', label: '기간', kind: 'single', defaultValue: 'today',
        choices: [{ value: 'today', label: '오늘' }, { value: 'today2', label: '오늘 + 내일' }, { value: 'week', label: '이번 주' }],
      },
    ],
  },

  /* 오늘 할 일 — 현행: 결재 · 오늘 마감 · 기한 지남 · 오늘 일정 · 0건 줄 없음 (`WorkDetailWidgets.tsx:247-275`). */
  'today-todo': {
    widgetKey: 'today-todo',
    groups: [ITEMS],
    items: [
      { key: 'approval', group: 'items', label: '결재할 문서', defaultOn: true, requires: { menus: [MENU.approval] } },
      { key: 'taskToday', group: 'items', label: '오늘 마감 태스크', defaultOn: true, requires: { menus: [MENU.task] } },
      { key: 'taskOverdue', group: 'items', label: '기한 지난 태스크', defaultOn: true, requires: { menus: [MENU.task] } },
      { key: 'event', group: 'items', label: '오늘 일정', defaultOn: true, requires: { menus: [MENU.calendar] } },
      {
        key: 'confirmNotice', group: 'items', label: '확인 요청 공지', desc: '게시판에서 내가 확인할 공지', defaultOn: false,
        requires: { server: 'board-confirm-mine' }, lockText: '게시판 확인 요청이 준비되면 고를 수 있습니다',
      },
      { key: 'unreadMail', group: 'items', label: '안 읽은 메일', defaultOn: false, requires: { menus: [MENU.mail] } },
    ],
    options: [
      { key: 'hideEmpty', label: '0건 줄 숨기기', kind: 'toggle', desc: '할 일이 없는 항목은 줄을 세우지 않습니다', defaultValue: true },
    ],
  },

  /* 전자결재 진행현황 — 현행: 내가 올린 문서 3건 · 상태 거르지 않음 · 줄에 결재선 진행 (`WorkWidgets.tsx:25-66`). 머리 칩 없음(W-Q3 — 새로 달지 않는다). */
  approval: {
    widgetKey: 'approval',
    groups: [{ key: 'line', label: '한 줄에 보일 것' }],
    items: [
      { key: 'progress', group: 'line', label: '결재선 진행', defaultOn: true },
      { key: 'waiting', group: 'line', label: '지금 대기자 이름', defaultOn: false },
      {
        key: 'rejectReason', group: 'line', label: '반려 사유', desc: '반려 상태를 고를 때만 보입니다', defaultOn: false,
        requires: { server: 'reject-comment' },
      },
      { key: 'draftedAt', group: 'line', label: '올린 날짜', defaultOn: false },
    ],
    options: [
      {
        key: 'count', label: '표시 개수', kind: 'single', defaultValue: '3',
        choices: [{ value: '3', label: '3건' }, { value: '5', label: '5건' }],
      },
      {
        key: 'view', label: '보여 줄 문서', kind: 'single', defaultValue: 'drafted',
        choices: [{ value: 'drafted', label: '내가 올린 문서' }, { value: 'incoming', label: '내가 결재할 문서' }, { value: 'both', label: '둘 다' }],
      },
      {
        key: 'status', label: '문서 상태', kind: 'multi',
        defaultValue: ['IN_PROGRESS', 'REJECTED', 'APPROVED', 'DRAFT', 'RECALLED'],
        choices: [
          { value: 'IN_PROGRESS', label: '진행 중' },
          { value: 'REJECTED', label: '반려' },
          { value: 'APPROVED', label: '완료' },
          { value: 'DRAFT', label: '임시저장' },
          { value: 'RECALLED', label: '회수' },
        ],
      },
    ],
  },

  /* 팀 근무 현황 — 현행: 상태 칩 · 사람 6명 · 상태 순서 정렬 · 서버 기본 범위 (`WorkDetailWidgets.tsx:48-103`). 상태 칩은 표시일 뿐 필터가 아니다. */
  'team-presence': {
    widgetKey: 'team-presence',
    groups: [ITEMS],
    items: [
      { key: 'counts', group: 'items', label: '상태별 인원 칩', defaultOn: true },
      { key: 'list', group: 'items', label: '사람 목록', defaultOn: true },
      { key: 'absentFirst', group: 'items', label: '미출근만 먼저', desc: '정렬 · 끄면 상태 순서', defaultOn: false },
    ],
    options: [
      {
        key: 'count', label: '표시 인원', kind: 'single', defaultValue: '6',
        choices: [{ value: '6', label: '6명' }, { value: '9', label: '9명' }, { value: '12', label: '12명' }],
      },
      {
        key: 'scope', label: '범위', kind: 'single', defaultValue: 'team',
        hint: '부서는 부서장 · 전사는 근태 담당자 · 관리자만 고릅니다(서버 권한과 같음)',
        choices: [
          { value: 'team', label: '내 팀' },
          { value: 'division', label: '부서', hiddenUntil: 'division-scope', requires: { scope: 'division' }, lockText: '부서는 부서장만 고를 수 있습니다' },
          { value: 'company', label: '전사', requires: { scope: 'company' }, lockText: '전사는 근태 담당자 · 관리자만 고를 수 있습니다' },
        ],
      },
      {
        key: 'statuses', label: '보일 상태', kind: 'multi',
        defaultValue: ['WORKING', 'DEEMED', 'LEAVE', 'OFF', 'ABSENT'],
        choices: [
          { value: 'WORKING', label: '근무 중' },
          { value: 'DEEMED', label: '출장·외근' },
          { value: 'LEAVE', label: '휴가' },
          { value: 'OFF', label: '퇴근' },
          { value: 'ABSENT', label: '미출근' },
        ],
      },
    ],
  },
};

/** 보이는 항목을 고칠 수 있는 위젯인가 (1단계 8종). */
export function hasItemSchema(widgetKey: string): boolean {
  return Object.prototype.hasOwnProperty.call(WIDGET_ITEM_SCHEMAS, widgetKey);
}

export function getItemSchema(widgetKey: string): WidgetItemSchema | null {
  return hasItemSchema(widgetKey) ? WIDGET_ITEM_SCHEMAS[widgetKey]! : null;
}

/* ── 읽기 ─────────────────────────────────────────────────────────────────── */

export interface ResolvedItem {
  def: ItemDef;
  /** 저장값(없으면 기본값) — 막혀도 그대로다. */
  on: boolean;
  lock: Lock | null;
  /** 지금 그릴지 = 켜짐 · 안 막힘 · 묶음 최대 개수 안. */
  visible: boolean;
}

/**
 * 항목을 **표시 순서대로** 푼다.
 * 순서 = 저장값에 있는 key 들의 순서 → 저장값에 없는 항목은 정의 순서대로 뒤에.
 */
export function resolveItems(
  schema: WidgetItemSchema,
  settings: WidgetSettings | undefined,
  ctx: ItemContext = {},
  group?: string,
): ResolvedItem[] {
  const stored = Array.isArray(settings?.items) ? settings!.items : [];
  const defs = schema.items.filter((d) => group === undefined || d.group === group);
  const byKey = new Map(defs.map((d) => [d.key, d]));
  const storedOn = new Map<string, boolean>();
  const ordered: ItemDef[] = [];
  for (const s of stored) {
    if (!s || typeof s.key !== 'string') continue;
    const def = byKey.get(s.key);
    if (!def || storedOn.has(s.key)) continue;
    storedOn.set(s.key, s.on === true);
    ordered.push(def);
  }
  for (const d of defs) if (!storedOn.has(d.key)) ordered.push(d);

  const shownPerGroup = new Map<string, number>();
  return ordered.map((def) => {
    const on = storedOn.get(def.key) ?? def.defaultOn;
    const lock = lockOf(def.requires, def.lockText, ctx);
    const max = schema.groups.find((g) => g.key === def.group)?.maxOn;
    let visible = on && !lock;
    if (visible && max !== undefined) {
      const n = shownPerGroup.get(def.group) ?? 0;
      if (n >= max) visible = false; else shownPerGroup.set(def.group, n + 1);
    }
    return { def, on, lock, visible };
  });
}

/** 한 항목을 지금 그릴지 — 위젯 렌더가 쓴다. 정의에 없는 key 는 false. */
export function isItemVisible(
  schema: WidgetItemSchema, settings: WidgetSettings | undefined, key: string, ctx: ItemContext = {},
): boolean {
  return resolveItems(schema, settings, ctx).find((r) => r.def.key === key)?.visible ?? false;
}

/** 지금 그릴 항목 key 를 표시 순서대로. */
export function visibleItemKeys(
  schema: WidgetItemSchema, settings: WidgetSettings | undefined, ctx: ItemContext = {}, group?: string,
): string[] {
  return resolveItems(schema, settings, ctx, group).filter((r) => r.visible).map((r) => r.def.key);
}

export interface ResolvedChoice {
  def: ChoiceDef;
  lock: Lock | null;
}

/** 선택지 — 숨김(`hiddenUntil`)은 빼고, 막힘은 이유와 함께 준다. */
export function resolveChoices(option: OptionDef, ctx: ItemContext = {}): ResolvedChoice[] {
  if (option.kind === 'toggle') return [];
  return option.choices
    .filter((c) => !c.hiddenUntil || ctx.serverFeatures?.has(c.hiddenUntil))
    .map((c) => ({ def: c, lock: lockOf(c.requires, c.lockText, ctx) }));
}

/**
 * 선택지의 **지금 쓸 값**.
 * ★ 저장값이 틀렸거나(모르는 값 · 모양이 다름) 지금 막힌 선택지면 기본값으로 떨어진다 — 저장값은 안 지운다(규칙 D2).
 */
export function resolveOption(
  schema: WidgetItemSchema, settings: WidgetSettings | undefined, key: string, ctx: ItemContext = {},
): WidgetOptionValue | undefined {
  const option = schema.options.find((o) => o.key === key);
  if (!option) return undefined;
  const raw = settings?.options?.[key];
  if (option.kind === 'toggle') return typeof raw === 'boolean' ? raw : option.defaultValue;
  const usable = new Set(resolveChoices(option, ctx).filter((c) => !c.lock).map((c) => c.def.value));
  if (option.kind === 'single') {
    return typeof raw === 'string' && usable.has(raw) ? raw : option.defaultValue;
  }
  if (!Array.isArray(raw)) return option.defaultValue;
  return option.choices.map((c) => c.value).filter((v) => raw.includes(v) && usable.has(v));
}

/* ── 「바뀜 n」 ─────────────────────────────────────────────────────────────── */

/**
 * 기본값과 다른 칸의 수 — 설정 창 왼쪽 목록의 「바뀜 n」.
 * 센다: 켜기/끄기가 기본과 다른 항목 하나씩 · 묶음 순서가 기본과 다르면 묶음마다 하나 ·
 *       유효한 저장값이 기본과 다른 선택지 하나씩 · 표시 이름 · 높이.
 * ★ 막힘과 무관하게 **저장값**으로 센다 — 막혀서 안 보이는 것도 사용자가 바꾼 것이다.
 */
export function changedCount(schema: WidgetItemSchema, settings: WidgetSettings | undefined): number {
  let n = 0;
  for (const g of schema.groups) {
    const resolved = resolveItems(schema, settings, {}, g.key);
    n += resolved.filter((r) => r.on !== r.def.defaultOn).length;
    const order = resolved.map((r) => r.def.key).join(',');
    const base = schema.items.filter((d) => d.group === g.key).map((d) => d.key).join(',');
    if (order !== base) n += 1;
  }
  const all: ItemContext = { scopes: { team: true, division: true, company: true }, serverFeatures: ALL_FEATURES };
  for (const o of schema.options) {
    if (settings?.options?.[o.key] === undefined) continue;
    const v = resolveOption(schema, settings, o.key, all);
    if (!sameValue(v, o.defaultValue)) n += 1;
  }
  if (typeof settings?.title === 'string' && settings.title.trim() !== '') n += 1;
  if (settings?.height !== undefined) n += 1;
  return n;
}

const ALL_FEATURES: ReadonlySet<ServerFeature> = new Set<ServerFeature>([
  'board-confirm-mine', 'focus-time', 'reject-comment', 'attendance-year',
  'attendance-team-summary', 'week-summary-team', 'division-scope',
]);

function sameValue(a: WidgetOptionValue | undefined, b: WidgetOptionValue): boolean {
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && [...a].sort().join(' ') === [...b].sort().join(' ');
  }
  return a === b;
}

/* ── 쓰기 (store 의 `setWidgetSettings` 에 넘길 조각) ─────────────────────────── */

/**
 * 지금 순서 그대로 전체 목록을 만든다. 정의에 없는 저장 key 는 **뒤에 그대로 붙인다**(M3 — 버리지 않는다).
 */
function fullItems(schema: WidgetItemSchema, settings: WidgetSettings | undefined): WidgetItemSetting[] {
  const known = schema.groups.flatMap((g) =>
    resolveItems(schema, settings, {}, g.key).map((r) => ({ key: r.def.key, on: r.on })));
  const knownKeys = new Set(schema.items.map((d) => d.key));
  const unknown = (Array.isArray(settings?.items) ? settings!.items : [])
    .filter((s) => s && typeof s.key === 'string' && !knownKeys.has(s.key));
  return [...known, ...unknown];
}

/** 항목 켜기/끄기. 묶음의 `maxOn` 을 넘기거나 `minOn` 아래로 내리는 변경은 **무시**하고 `null` 을 준다. */
export function setItemOn(
  schema: WidgetItemSchema, settings: WidgetSettings | undefined, key: string, on: boolean,
): Pick<WidgetSettings, 'items'> | null {
  const def = schema.items.find((d) => d.key === key);
  if (!def) return null;
  const group = schema.groups.find((g) => g.key === def.group);
  const inGroup = resolveItems(schema, settings, {}, def.group);
  const current = inGroup.find((r) => r.def.key === key)!;
  if (current.on === on) return null;
  const onCount = inGroup.filter((r) => r.on).length;
  if (on && group?.maxOn !== undefined && onCount >= group.maxOn) return null;
  if (!on && group?.minOn !== undefined && onCount <= group.minOn) return null;
  return { items: fullItems(schema, settings).map((s) => (s.key === key ? { ...s, on } : s)) };
}

/**
 * 묶음 안에서 순서 옮기기 — 끌기와 키보드(↑↓)가 같은 함수를 쓴다(Q-1).
 * `from` · `to` 는 **그 묶음 안의 자리**다. 범위를 벗어나거나 같은 자리면 `null`.
 */
export function moveItem(
  schema: WidgetItemSchema, settings: WidgetSettings | undefined, group: string, from: number, to: number,
): Pick<WidgetSettings, 'items'> | null {
  const inGroup = resolveItems(schema, settings, {}, group).map((r) => ({ key: r.def.key, on: r.on }));
  if (from === to || from < 0 || to < 0 || from >= inGroup.length || to >= inGroup.length) return null;
  const moved = [...inGroup];
  const [x] = moved.splice(from, 1);
  moved.splice(to, 0, x!);
  // 다른 묶음 · 모르는 key 는 제자리. 이 묶음 칸만 새 순서로 갈아 끼운다.
  const groupKeys = new Set(inGroup.map((s) => s.key));
  let i = 0;
  return {
    items: fullItems(schema, settings).map((s) => (groupKeys.has(s.key) ? moved[i++]! : s)),
  };
}

/** 선택지 하나 바꾸기 — 다른 선택지(모르는 key 포함)는 그대로 둔다. */
export function setOption(
  settings: WidgetSettings | undefined, key: string, value: WidgetOptionValue,
): Pick<WidgetSettings, 'options'> {
  return { options: { ...(settings?.options ?? {}), [key]: value } };
}

/** 「이 위젯 기본값으로」 중 보이는 항목 · 선택지 부분. 이름 · 높이 · 위젯 고유 설정은 부르는 쪽이 정한다. */
export const ITEMS_RESET_PATCH: Pick<WidgetSettings, 'items' | 'options'> = { items: undefined, options: undefined };
